import React, {Fragment, useEffect, useState} from "react";
import {useEvents} from "../hooks/useEvents";
import {Link, useLocation, useNavigate} from "react-router-dom";

const EventsPage = () => {
    const location = useLocation();
    const navigate = useNavigate();
    // Extract date from query parameters or default to today's date
    const queryParams = new URLSearchParams(location.search);
    const initialDate = queryParams.get("date") || new Date().toISOString().split("T")[0];

    const [choosedDate, setChoosedDate] = useState(initialDate);
    const {events, continuousEventDates, weekDates, prevWeekDate, nextWeekDate} = useEvents(
        choosedDate,
        0
    );

    useEffect(() => {
        navigate(`?date=${choosedDate}`, {replace : true});

        window.scrollTo(0, 0);
    }, [choosedDate, navigate]);

    if (weekDates.length === 0) {
        return <div>Loading...</div>;
    }

    const firstDate = new Date(weekDates[0]);
    const monthName = firstDate.toLocaleDateString("de-DE", {month : "long"});

    const groupedEvents = continuousEventDates.map((date) => ({
        date,
        events : events.filter((event) => event.eventDate === date),
    }));

    const handlePrevWeek = () => {
        setChoosedDate(prevWeekDate);
    };

    const handleNextWeek = () => {
        setChoosedDate(nextWeekDate);
    };

    return (
        <Fragment>
            <div className="max-w-4xl mx-auto p-4 bg-white rounded-lg shadow-md">
                <div className="relative mb-6 flex items-center justify-center">
                    {/* Previous Week Button */}
                    <button
                        onClick={handlePrevWeek}
                        className="absolute left-0 text-gray-500 hover:text-pegasusPurpleHover px-4 py-2 rounded-lg bg-gray-100 hover:bg-pink-100"
                    >
                        &lt;
                    </button>

                    {/* Month Name */}
                    <h2 className="text-2xl font-semibold text-gray-800">
                        {monthName.charAt(0).toUpperCase() + monthName.slice(1)}
                    </h2>

                    {/* Next Week Button */}
                    <button
                        onClick={handleNextWeek}
                        className="absolute right-0 text-gray-500 hover:text-pegasusPurpleHover px-4 py-2 rounded-lg bg-gray-100 hover:bg-pink-100"
                    >
                        &gt;
                    </button>
                </div>

                {/* Week Header */}
                <div className="flex space-x-4 justify-center mb-6">
                    {weekDates.map((date, index) => {
                        const dayName = new Date(date).toLocaleDateString("de-DE", {weekday : "short"});
                        const day = new Date(date).toLocaleDateString("de-DE", {day : "2-digit"});
                        return (
                            <div
                                key={index}
                                className="flex flex-col items-center text-gray-600"
                            >
                                {/* Day Name */}
                                <span className="text-xs font-medium uppercase">
                                    {dayName.substring(0, 2)}
                                 </span>
                                {/* Date */}
                                <span className={`px-2 py-1 rounded-lg`}>
                                    {day}
                                </span>
                            </div>
                        );
                    })}
                </div>
                {/* Events by Day */}
                <div>
                    {groupedEvents.map(({date, events}) => (
                        <div key={date} className="mb-6">
                            {/* Date Heading */}
                            <h3 className="text-lg font-semibold text-gray-800 text-center mb-4">
                                {new Date(date).toLocaleDateString("de-DE", {
                                    weekday : "long",
                                    day     : "2-digit",
                                    month   : "long",
                                    year    : "numeric",
                                })}
                            </h3>

                            <div className="space-y-4">
                                {events.length > 0 ? (
                                    events.map((event) => (
                                        <Link
                                            to={`/events/${event.eventUuid}`}
                                            key={event.eventUuid}
                                            className={`flex items-center shadow rounded-lg border border-gray-200 ${
                                                event.requiresSmartLock
                                                    ? "bg-[#F5F5F7] hover:bg-[#ECECF0]"
                                                    : "bg-white hover:bg-[#FDF2F8]"
                                            }`}
                                        >
                                            {/* Time Block */}
                                            <div className="flex items-center justify-center w-20 bg-pink-100 relative">
                                                <div
                                                    className="absolute top-0 bottom-0 right-0 w-1"
                                                    style={{ backgroundColor: event.color || "#ff69b4" }}
                                                ></div>
                                                <div className="flex flex-col items-center justify-center w-full py-4">
                                                    <p className="text-sm font-medium text-gray-800">{event.startTime}</p>
                                                    <p className="text-xs text-gray-500">{event.endTime}</p>
                                                </div>
                                            </div>

                                            {/* Event Details */}
                                            <div className="flex-1 px-4 py-3">
                                                <div className="flex flex-col">
                                                    {/* Event Title */}
                                                    <h4 className="font-semibold text-gray-800">
                                                        {event.name}
                                                        {event.bookedAt && (
                                                            <span className="ml-2 text-pegasusPurpleHover text-sm font-bold">
                                                                &#10003;
                                                            </span>
                                                        )}
                                                    </h4>
                                                    {/* Event Info */}
                                                    <p className="text-sm text-gray-600">
                                                        {`${event.trainerNames} • ${event.locationName} • `}
                                                        {event.bookedAt ? (
                                                            "Gebucht"
                                                        ) : event.freeSeats === 0 ? (
                                                            <span className="font-bold">Ausgebucht</span>
                                                        ) : event.freeSeats === 1 ? (
                                                            <span className="font-bold">Nur noch 1 Platz frei</span>
                                                        ) : event.freeSeats <= 3 ? (
                                                            <span className="font-bold">
                                                                Nur noch {event.freeSeats} Plätze frei
                                                            </span>
                                                        ) : (
                                                            `${event.freeSeats} Plätze frei`
                                                        )}
                                                    </p>
                                                </div>
                                            </div>
                                        </Link>
                                    ))
                                ) : (
                                    <p className="text-sm text-gray-500 text-center">
                                        Keine Events an diesem Tag
                                    </p>
                                )}
                            </div>
                        </div>
                    ))}
                    {groupedEvents.length === 0 && (
                        <p className="text-sm text-gray-500 text-center">Keine Events in dieser Woche</p>
                    )}
                </div>
            </div>
        </Fragment>
    );
};

export default EventsPage;
